"use client";

import * as React from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { cn } from "@/lib/utils";

export interface PopoverSelectOption {
  value: string;
  label: string;
}

interface PopoverSelectProps {
  /**
   * Forwarded to the trigger so a `<label htmlFor>` can point at it.
   *
   * The trigger is a button, not an input, so without this a label that used to be bound to
   * a native <select> silently stops being clickable — and stops being announced with the
   * control — the moment it is swapped for this component.
   */
  id?: string;
  value: string;
  onValueChange: (value: string) => void;
  options: PopoverSelectOption[];
  placeholder?: string;
  disabled?: boolean;
  /** Box styling for the trigger (border / padding / font). Text colour is handled internally. */
  triggerClassName?: string;
  /**
   * Optional overrides for the trigger's selected value, its placeholder and its chevron.
   * Each REPLACES the default rather than adding to it, so a caller cannot end up with two
   * colour utilities on one element. Omitted = exactly the look every widget has today.
   */
  valueClassName?: string;
  placeholderClassName?: string;
  iconClassName?: string;
  contentClassName?: string;
  ariaLabel?: string;
  /**
   * Group heading pinned above the options, as BDU's select does with
   * `<SelectGroup><SelectLabel>Interest *</SelectLabel>`. Omitted everywhere by
   * default, so a consumer that does not ask for one renders exactly as before.
   */
  heading?: string;
  /** Optional: notified when the dropdown opens/closes (e.g. for analytics). */
  onOpenChange?: (open: boolean) => void;
}

/**
 * A styled select built on Radix **Popover** (`modal={false}`) instead of Radix
 * Select.
 *
 * Radix Select always mounts `react-remove-scroll`, which locks WINDOW scroll
 * while the dropdown is open and exposes no prop to disable it — so on an
 * embedded form (e.g. Lead Drop 9 in Banner 10) the whole page can't scroll
 * while a dropdown is open. Popover with `modal={false}` does NOT lock scroll,
 * so the page (and the inline form) stay scrollable. The trigger + option list
 * are styled to match the previous Select dropdown look.
 */
export function PopoverSelect({
  id,
  value,
  onValueChange,
  options,
  placeholder,
  disabled,
  triggerClassName,
  valueClassName,
  placeholderClassName,
  iconClassName,
  contentClassName,
  ariaLabel,
  heading,
  onOpenChange,
}: PopoverSelectProps) {
  const [open, setOpen] = React.useState(false);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  /**
   * The element the option list is portalled into.
   *
   * `null` means document.body, which is right on a page and WRONG inside a modal dialog.
   * Radix Dialog defaults to `modal`, which mounts `react-remove-scroll`; that blocks wheel
   * events on everything outside the dialog's own subtree. A list portalled to the body is
   * "outside", so it renders a scrollbar and then refuses to scroll — the exact symptom on
   * the Lead Drop 9 popup. Portalling into the dialog puts it back inside the region the
   * lock permits. Note this component already avoids Radix Select for a related reason
   * (see the note above); the Dialog mounts the same lock regardless, so the select has to
   * cooperate with it rather than dodge it.
   *
   * Resolved in an effect, not during render: the ancestor exists only on the client, and a
   * DOM read while rendering would disagree with the server's markup.
   */
  const [portalContainer, setPortalContainer] = React.useState<HTMLElement | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setPortalContainer(triggerRef.current?.closest<HTMLElement>('[role="dialog"]') ?? null);
  }, [open]);
  const itemRefs = React.useRef<(HTMLButtonElement | null)[]>([]);
  const selectedIndex = options.findIndex((o) => o.value === value);
  const selected = selectedIndex >= 0 ? options[selectedIndex] : undefined;

  const focusItem = (index: number) => {
    const n = options.length;
    if (n === 0) return;
    const i = ((index % n) + n) % n;
    itemRefs.current[i]?.focus();
  };

  return (
    <PopoverPrimitive.Root
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        onOpenChange?.(o);
      }}
      modal={false}
    >
      <PopoverPrimitive.Trigger
        ref={triggerRef}
        id={id}
        type="button"
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        className={cn("flex w-full items-center justify-between gap-2", triggerClassName)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault();
            setOpen(true);
            requestAnimationFrame(() =>
              focusItem(
                e.key === "ArrowDown" ? Math.max(0, selectedIndex) : options.length - 1,
              ),
            );
          }
        }}
      >
        <span
          className={cn(
            /* `line-clamp-1`, not `truncate`. Both keep the trigger one line
               tall, but `truncate` is `white-space: nowrap` + ellipsis, which
               cuts THROUGH a word ("Online MA Public Administrati…").
               `line-clamp-1` wraps normally and then clips the line, so the
               last word shown is whole. This is what the reference does — its
               select trigger carries `[&>span]:line-clamp-1`. */
            "min-w-0 text-left [overflow-wrap:anywhere] line-clamp-1",
            selected ? (valueClassName ?? "text-[#101112]") : (placeholderClassName ?? "text-[#888]"),
          )}
        >
          {selected ? selected.label : placeholder}
        </span>
        <svg
          className={iconClassName ?? "size-4 shrink-0 text-[#888]"}
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden
        >
          <path
            d="M6 9l6 6 6-6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal container={portalContainer ?? undefined}>
        <PopoverPrimitive.Content
          role="listbox"
          align="start"
          sideOffset={4}
          /* Keep the list clear of the viewport edges so it never runs off the
             bottom of the screen (or out from under a dialog) the way a fixed
             height does. */
          collisionPadding={12}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            requestAnimationFrame(() => focusItem(selectedIndex >= 0 ? selectedIndex : 0));
          }}
          className={cn(
            "z-[200] w-[var(--radix-popover-trigger-width)] rounded-lg border border-[#ededed] bg-white p-1 shadow-lg",
            /* DEFAULT: the smaller of 280px and whatever room is actually left
               below the trigger — Radix measures the latter into
               `--radix-popover-content-available-height`. Without that clamp a
               flat cap let the list hang past the bottom of the form card.
               OPT-OUT: a caller that declares the --ps-max-h variable replaces the
               whole expression, clamp included, and gets a flat cap — which is
               what BDU's select does (`max-h-96`, no clamp). Inside a popup the
               available height below the trigger is small, so the clamp, not
               the cap, is what forces a scrollbar; raising only the cap changes
               nothing there.
               One utility, redeclared by a variable: a second `max-h-*` would
               collide and the winner would be Tailwind's emission order. */
            "max-h-[var(--ps-max-h,min(280px,var(--radix-popover-content-available-height)))] overflow-y-auto",
            /* Without this, reaching the end of the list hands the wheel to the
               page behind, so the page scrolls while the dropdown looks stuck. */
            "overscroll-contain",
            contentClassName,
          )}
        >
          {heading ? (
            <div
              /* Not an <option>: a label, so screen readers do not offer it as a
                 choice and the keyboard walk skips straight to the first item. */
              role="presentation"
              className="px-3 py-1.5 text-xs font-semibold text-[#101112]"
            >
              {heading}
            </div>
          ) : null}
          {options.map((opt, i) => {
            const isSelected = opt.value === value;
            return (
              <button
                key={opt.value}
                ref={(el) => {
                  itemRefs.current[i] = el;
                }}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => {
                  onValueChange(opt.value);
                  setOpen(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === "ArrowDown") {
                    e.preventDefault();
                    focusItem(i + 1);
                  } else if (e.key === "ArrowUp") {
                    e.preventDefault();
                    focusItem(i - 1);
                  }
                }}
                className={cn(
                  /* `items-start`: once a long label wraps, a centred tick would
                     float beside the middle of the block instead of the first line. */
                  "flex w-full cursor-pointer items-start justify-between rounded-[6px] px-3 py-2 text-left text-[#101112] outline-none hover:bg-[#f5f5f5] focus:bg-[#f5f5f5]",
                  isSelected && "bg-[#f5f5f5] font-medium",
                )}
              >
                {/* NOT truncated. There is room to wrap here, and the reference
                    clips nothing in its option list — cutting a course name in
                    half is worse than a two-line row. */}
                <span className="min-w-0 [overflow-wrap:anywhere]">{opt.label}</span>
                {isSelected && (
                  <svg
                    className="ml-2 size-4 shrink-0 text-[#9F1C34]"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden
                  >
                    <path
                      d="M5 13l4 4L19 7"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </button>
            );
          })}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
